import React, { useState, useEffect, useCallback } from 'react';
import { getCategories, getProducts } from '../dataManager';
import useBidirectionalScroll from '../hooks/useBidirectionalScroll';
import about_section from '../assets/about_section.png';
import whychooseus from '../assets/why_choose_us.png';
import ProductCard from './ProductCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faWrench, faGlobe, faMicrochip, faUserCheck } from '@fortawesome/free-solid-svg-icons';

// Swiper for 3D Catalog
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

// Product Images for Hero
import hero1 from '../assets/hero_slide1.jpg';
import hero2 from '../assets/hero_slide2.jpg';
import hero3 from '../assets/hero_slide3.jpg';
import heroPress from '../assets/hero_press_slide.png';
import udyamCert from '../assets/udyam_cert.jpg';
import iecCert from '../assets/iec_cert.jpg';
import gstCert from '../assets/gst_cert.jpg';

// Internal Typewriter component
const TypewriterText = ({ text, speed = 100 }) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      setDisplayedText(text.slice(0, index));
      index++;
      if (index > text.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);

  return (
    <span className="notranslate" translate="no">
      <span>{displayedText}</span>
      <span className="typewriter-cursor">|</span>
    </span>
  );
};

// Stat Counter Component
const StatCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = React.useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const endValue = parseInt(end.replace(/,/g, ''));
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing: easeOutExpo
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * endValue));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [visible, end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};

export default function Home({ navigateTo }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);

  const [categories] = useState(() => getCategories());
  const [productsList] = useState(() => getProducts());

  const sliderImages = [
    { src: heroPress, title: "Advanced Hydraulic Machinery" },
    { src: hero1, title: "Precision Polishing Systems" },
    { src: hero2, title: "High-Performance Rolling Mills" },
    { src: hero3, title: "Specialized Tooling Solutions" }
  ];

  const nextSlide = useCallback(() => setCurrentSlide((prev) => (prev + 1) % sliderImages.length), [sliderImages.length]);
  const prevSlide = useCallback(() => setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length), [sliderImages.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useBidirectionalScroll(null, 0);

  return (
    <>
      <section id="home" className="hero home-hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* 3D Interactive Parallax Background Typography */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <div className="parallax-bg-text" style={{
            fontSize: 'min(20vw, 250px)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(10, 102, 255, 0.08)',
            letterSpacing: '10px',
            transform: `translate(${mousePos.x * 50}px, ${mousePos.y * 50}px)`,
            transition: 'transform 0.1s ease-out',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            N.D. TOOLS
          </div>
        </div>

        {/* Ambient Grid Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(10, 102, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 102, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
          transition: 'transform 0.15s ease-out',
        }}></div>

        <div className="hero-background-slider" style={{ zIndex: 2, transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)`, transition: 'transform 0.2s ease-out' }}>
          {sliderImages.map((slide, index) => (
            <div
              key={index}
              className={`full-slide ${index === currentSlide ? 'active' : ''}`}
            >
              <div className="bg-img-container">
                <img src={slide.src} alt={slide.title} className="bg-img" />
              </div>
              <div className="bg-overlay"></div>
            </div>
          ))}
        </div>

        <div className="hero-content">
          <h1 className="hero-title">
            N.D. Tools & Machinery<br />
            <span>Jewellery Engineering Equipment</span>
          </h1>
          <p className="hero-description">
            <strong style={{ color: "var(--accent-color)" }}>
              <TypewriterText text={sliderImages[currentSlide].title} speed={70} />
            </strong>
            <br /><br />
            Equip your industrial operations with the highest standard of durability and accuracy. N.D.Tools delivers uncompromised engineering quality directly to your workspace.
          </p>
          {/* <div className="hero-cta-buttons animate-hidden delay-300" style={{ display: 'flex', gap: '1.5rem', marginTop: '2rem' }}>
            <button 
              className="primary-button" 
              onClick={() => {
                const productsSection = document.getElementById('products');
                if (productsSection) {
                  productsSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigateTo('products');
                }
              }}
              style={{ maxWidth: '240px' }}
            >
              Explore Catalog
            </button>
            <button 
              className="outline-button" 
              onClick={() => navigateTo('ebrochure')}
              style={{ padding: '0.9rem 1.5rem' }}
            >
              Download Brochure
            </button>
          </div> */}
        </div>



        <div className="hero-slider-controls">
          <button onClick={prevSlide} className="slider-btn" aria-label="Previous Slide">&#8592;</button>
          <div className="slider-indicators">
            {sliderImages.map((_, idx) => (
              <span key={idx} className={`indicator-line ${idx === currentSlide ? 'active' : ''}`} onClick={() => setCurrentSlide(idx)}></span>
            ))}
          </div>
          <button onClick={nextSlide} className="slider-btn" aria-label="Next Slide">&#8594;</button>
        </div>
      </section>

      <section className="stats-banner">
        <div className="stat-item animate-hidden">
          <h3 className="stat-number notranslate" translate="no"><StatCounter end="21" suffix="+" /></h3>
          <p className="stat-label">Years Experience</p>
        </div>
        <div className="stat-item animate-hidden delay-100">
          <h3 className="stat-number notranslate" translate="no"><StatCounter end="10000" suffix="+" /></h3>
          <p className="stat-label">Happy Customers</p>
        </div>
        <div className="stat-item animate-hidden delay-200">
          <h3 className="stat-number notranslate" translate="no"><StatCounter end="500" suffix="+" /></h3>
          <p className="stat-label">Machines Deployed</p>
        </div>
        <div className="stat-item animate-hidden delay-300">
          <h3 className="stat-number notranslate" translate="no">24/7</h3>
          <p className="stat-label">Customer Support</p>
        </div>
      </section>

      <section className="about-section" style={{ padding: '4rem 6rem', position: 'relative', overflow: 'hidden' }}>

        <div className="about-content" style={{ position: 'relative', zIndex: 1 }}>
          <div className="about-text animate-hidden animate-left">
            <div className="hero-badge" style={{ marginBottom: "1rem" }}>Our Organization</div>
            <h2 className="section-title">Engineering Excellence Since <br />2005</h2>
            <p className="about-description">
              At N.D.Tools, we specialize in the manufacturing and distribution of machinery. Our flagship hydraulic presses and commercial calibration units are trusted worldwide.
            </p>
            <button className="primary-button" onClick={() => navigateTo('about')}>Learn More</button>
          </div>
          <div className="about-image-wrapper animate-hidden animate-right delay-200" style={{ position: 'relative' }}>
            {/* Corner Decorative Squares */}
            <div style={{
              position: 'absolute',
              bottom: '-40px',
              left: '-40px',
              width: '120px',
              height: '120px',
              background: 'rgba(10, 102, 255, 0.15)',
              borderRadius: '12px',
              zIndex: 0,
              pointerEvents: 'none'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-15px',
              left: '-15px',
              width: '50px',
              height: '50px',
              background: 'var(--accent-color)',
              borderRadius: '6px',
              zIndex: 0,
              opacity: 0.8,
              pointerEvents: 'none'
            }}></div>
            <img src={about_section} alt="Industrial Equipment" className="about-image float-3d" style={{ position: 'relative', zIndex: 1 }} />
          </div>
        </div>
      </section>

      <div className="molten-divider-wrapper">
        <svg className="molten-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <defs>
            <linearGradient id="liquid-silver" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f4f9ff" />
              <stop offset="50%" stopColor="#dbeafe" />
              <stop offset="100%" stopColor="#0b1f3a" />
            </linearGradient>
          </defs>
          <path
            fill="url(#liquid-silver)"
            d="M0,160 C320,300,420,40,720,160 C1020,280,1120,60,1440,200 L1440,320 L0,320 Z"
          ></path>
        </svg>
      </div>

      <section id="products" className="products-section" style={{
        padding: '4rem 0',
        background: 'linear-gradient(135deg, #0b1f3a 0%, #1a365d 100%)',
        color: '#fff'
      }}>
        <div className="section-container">
          <div className="section-header">
            <div className="hero-badge" style={{ marginBottom: '1rem', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>Expertise Selection</div>
            <h2 className="section-title" style={{ color: '#fff' }}>Industrial <span style={{ color: 'var(--accent-color)' }}>Catalog</span></h2>
            <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>Select a category to explore our specialized machinery</p>
          </div>

          {/* 3D Coverflow Swiper */}
          <div className="catalog-swiper-wrapper" style={{ padding: '1rem 0', position: 'relative' }}>
            {/* Custom Navigation Arrows */}
            <button className="swiper-custom-prev swiper-nav-arrow" aria-label="Previous">&#8592;</button>
            <button className="swiper-custom-next swiper-nav-arrow" aria-label="Next">&#8594;</button>

            <Swiper
              key={categories.length}
              modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
              effect={'coverflow'}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={'auto'}
              coverflowEffect={{
                rotate: 35,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              pagination={{ clickable: true }}
              navigation={{
                prevEl: '.swiper-custom-prev',
                nextEl: '.swiper-custom-next',
              }}
              autoplay={{ delay: 5000 }}
              loop={true}
              className="category-swiper"
              breakpoints={{
                320: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1200: { slidesPerView: 3 }
              }}
            >
              {categories.map((cat) => {
                const count = productsList.filter(p => p.categoryId === cat.id).length;
                return (
                  <SwiperSlide key={cat.id} style={{ height: 'auto' }}>
                    <ProductCard
                      cat={cat}
                      productCount={count}
                      navigateTo={navigateTo}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </section>

      <section className="certificates-section" style={{
        padding: '6rem 0',
        position: 'relative',
        zIndex: 1
      }}>
        <style>{`
          .cert-img-container:hover .cert-overlay {
            opacity: 1 !important;
          }
        `}</style>

        <div className="section-container" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="hero-badge" style={{ marginBottom: '1rem' }}>Quality Assurance</div>
          <h2 className="section-title">Our <span style={{ color: 'var(--accent-color)' }}>Certifications</span></h2>
          <p className="section-subtitle">Committed to global standards of manufacturing excellence</p>
        </div>

        {[
          {
            src: udyamCert,
            title: "Udyam Registration",
            desc: "MSME Government of India",
            details: "Official recognition by the Ministry of Micro, Small and Medium Enterprises, Government of India, certifying N.D. Industry as a registered manufacturing unit.",
            isReverse: false,
            bg: '#f8fafe',
            isDark: false
          },
          {
            src: iecCert,
            title: "IEC Certificate",
            desc: "Import Export Code",
            details: "Registration with the Directorate General of Foreign Trade (DGFT) allowing N.D. Industry to participate in international trade and export our premium tools globally.",
            isReverse: true,
            bg: 'var(--bg-secondary)',
            isDark: false
          },
          {
            src: gstCert,
            title: "GST Registration",
            desc: "Goods and Services Tax",
            details: "Standard tax registration for legal compliance and transparent business operations within the Indian industrial ecosystem.",
            isReverse: false,
            bg: '#f8fafe',
            isDark: false
          }
        ].map((cert, idx) => (
          <div
            key={idx}
            style={{
              background: cert.bg,
              padding: '6rem 0',
              borderTop: '1px solid var(--border-color)',
              borderBottom: '1px solid var(--border-color)'
            }}
          >
            <div
              className="vision-mission-row"
              style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: '0',
                display: 'flex',
                gap: '8rem',
                alignItems: 'center',
                flexDirection: cert.isReverse ? 'row-reverse' : 'row'
              }}
            >
              <div className={`vm-content animate-hidden ${cert.isReverse ? 'animate-right' : 'animate-left'}`} style={{ flex: 1 }}>
                <h3 className="section-title" style={{
                  display: 'flex',
                  gap: '1.5rem',
                  alignItems: 'center',
                  marginBottom: '2rem',
                  fontSize: '2rem',
                  color: cert.isDark ? '#ffffff' : '#0b1f3a'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: cert.isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(10, 102, 255, 0.1)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: cert.isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid var(--border-color)'
                  }}>
                    <span style={{ color: cert.isDark ? 'var(--accent-cool)' : 'var(--accent-color)', fontSize: '1.5rem', fontWeight: 'bold', fontFamily: 'var(--ff-display)' }}>{idx + 1}</span>
                  </div>
                  <span>{cert.title}</span>
                </h3>

                <p style={{
                  fontSize: '1.2rem',
                  color: cert.isDark ? 'var(--accent-cool)' : 'var(--accent-color)',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  fontFamily: 'var(--ff-body)'
                }}>{cert.desc}</p>

                <div style={{
                  padding: '2rem',
                  backgroundColor: cert.isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                  borderRadius: '16px',
                  border: cert.isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--border-color)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)'
                }}>
                  <p className="about-description" style={{ color: cert.isDark ? '#e2e8f0' : 'var(--text-main)', margin: 0, fontSize: '1.05rem', lineHeight: '1.8' }}>
                    {cert.details}
                  </p>
                </div>
              </div>

              <div className={`vm-image animate-hidden ${cert.isReverse ? 'animate-left' : 'animate-right'}`} style={{ flex: 1.2, position: 'relative' }}>
                {/* Corner Decorative Circles */}
                <div style={{
                  position: 'absolute',
                  bottom: '-40px',
                  left: '-40px',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  background: cert.isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(10, 102, 255, 0.15)',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-15px',
                  left: '-15px',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: cert.isDark ? 'var(--accent-cool)' : 'var(--accent-color)',
                  zIndex: 0,
                  opacity: 0.8,
                  pointerEvents: 'none'
                }}></div>
                <div
                  className="cert-img-container"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: cert.isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--border-color)',
                    boxShadow: cert.isDark ? '0 15px 35px rgba(0,0,0,0.4)' : '0 15px 35px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.4s ease'
                  }}
                  onClick={() => setSelectedImage(cert.src)}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <img src={cert.src} alt={cert.title} style={{ width: '100%', display: 'block' }} />
                  <div
                    className="cert-overlay"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to top, rgba(11, 31, 58, 0.8), transparent)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: 600,
                      border: '2px solid #fff',
                      padding: '0.5rem 1.5rem',
                      borderRadius: '30px',
                      backdropFilter: 'blur(5px)'
                    }}>View Certificate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="why-choose-us" style={{ position: 'relative', overflow: 'hidden' }}>

        <div className="section-container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div className="hero-badge" style={{ marginBottom: "1rem" }}>Our Commitment</div>
            <h2 className="section-title">Why Choose <span style={{ color: 'var(--accent-color)' }}>N.D. Tools?</span></h2>
            <p className="section-subtitle">Engineering Excellence & Uncompromising Quality Since 2005</p>
          </div>

          <div className="why-grid">
            <div className="why-image-wrapper animate-hidden animate-left" style={{ position: 'relative' }}>
              <div className="why-image-container" style={{ position: 'relative' }}>
                {/* Corner Decorative Squares */}
                <div style={{
                  position: 'absolute',
                  bottom: '-40px',
                  left: '-40px',
                  width: '120px',
                  height: '120px',
                  background: 'rgba(10, 102, 255, 0.15)',
                  borderRadius: '12px',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-15px',
                  left: '-15px',
                  width: '50px',
                  height: '50px',
                  background: 'var(--accent-color)',
                  borderRadius: '6px',
                  zIndex: 0,
                  opacity: 0.8,
                  pointerEvents: 'none'
                }}></div>
                <img src={whychooseus} alt="Industrial Precision" className="why-image" style={{ position: 'relative', zIndex: 1 }} />
                <div style={{
                  position: 'absolute',
                  top: '2rem',
                  right: '2rem',
                  width: '60px',
                  height: '60px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                  zIndex: 2
                }}>
                  <FontAwesomeIcon icon={faMicrochip} style={{ color: 'var(--accent-color)', fontSize: '1.8rem' }} />
                </div>
              </div>
            </div>

            <div className="why-content animate-hidden animate-right">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <FontAwesomeIcon icon={faUserCheck} />
                </div>
                <div className="benefit-text">
                  <h4>Maximum Satisfaction</h4>
                  <p>Trusted by over 10,000+ clients globally, we ensure your success with personalized service and reliable machinery performance.</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
                <div className="benefit-text">
                  <h4>Industrial Durability</h4>
                  <p>Built using high-grade materials and Japanese-standard engineering to withstand the most demanding heavy-duty environments.</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <FontAwesomeIcon icon={faWrench} />
                </div>
                <div className="benefit-text">
                  <h4>Technical Support</h4>
                  <p>Direct access to our engineering team for specialized consultation, ensuring your machinery operates at peak performance.</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <FontAwesomeIcon icon={faGlobe} />
                </div>
                <div className="benefit-text">
                  <h4>Global Expertise</h4>
                  <p>Exporting premium machinery to over 15 countries, we understand global standards and localized industrial needs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(10, 102, 255, 0.05)',
          filter: 'blur(50px)',
          zIndex: 0,
          pointerEvents: 'none'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(10, 102, 255, 0.05)',
          filter: 'blur(50px)',
          zIndex: 0,
          pointerEvents: 'none'
        }}></div>

        <div className="section-container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }} className="animate-hidden animate-up">
            <div className="hero-badge" style={{ marginBottom: "1rem" }}>Client Feedback</div>
            <h2 className="section-title">What Our <span style={{ color: 'var(--accent-color)' }}>Clients Say</span></h2>
            <p className="section-subtitle">Trusted by thousands of jewellery makers and industrial experts worldwide</p>
          </div>

          <div className="testimonials-grid">
            {[
              {
                name: "Rajesh Mehta",
                role: "CEO, Mehta Jewellery Works",
                text: "The precision and reliability of N.D. Tools' hydraulic presses have completely transformed our manufacturing efficiency. Outstanding durability!",
                rating: 5,
                avatar: "RM"
              },
              {
                name: "Sarah Jenkins",
                role: "Production Manager, Global Gems Inc.",
                text: "Their customer support is unmatched. We had a custom request, and the team at N.D. Tools executed it perfectly. Truly top-tier machinery.",
                rating: 5,
                avatar: "SJ"
              },
              {
                name: "Amit Sharma",
                role: "Technical Director, Precision Casting",
                text: "Japanese-standard engineering at its best. These tools operate continuously without any compromise on accuracy. Highly recommended!",
                rating: 5,
                avatar: "AS"
              }
            ].map((testimonial, idx) => (
              <div key={idx} className={`testimonial-card animate-hidden animate-up delay-${(idx + 1) * 100}`}>
                <div className="testimonial-quote-icon">“</div>
                <div className="testimonial-stars">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <span key={i} style={{ color: 'var(--accent-color)', fontSize: '1.2rem', marginRight: '2px' }}>★</span>
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{testimonial.avatar}</div>
                  <div className="testimonial-info">
                    <h4 className="testimonial-name">{testimonial.name}</h4>
                    <p className="testimonial-role">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>




      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <span className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</span>
          <img src={selectedImage} alt="Full Screen Item" className="lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}